import { useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Upload, Check } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Onboarding() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("package");

  // Superadmin mag geen eigen bedrijf aanmaken via onboarding — die hoort in
  // het beheerpaneel. Stuur ze naar het dashboard (ze hebben al bedrijven).
  if (user?.role === "SUPERADMIN") return <Navigate to="/dashboard" replace />;

  const [name, setName] = useState("");
  const [kvk, setKvk] = useState("");
  const [vat, setVat] = useState("");
  const [iban, setIban] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!name.trim()) {
      setError("Bedrijfsnaam is verplicht");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      if (kvk) formData.append("kvk", kvk.trim());
      if (vat) formData.append("vatNumber", vat.trim());
      if (iban) formData.append("iban", iban.trim());
      if (address) formData.append("address", address.trim());
      if (zip) formData.append("zipCode", zip.trim());
      if (city) formData.append("city", city.trim());
      if (logo) formData.append("logo", logo);

      const { data: company } = await api.post("/api/companies", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      localStorage.setItem("aiboek_company_id", company.id);

      if (packageId) {
        await api.post("/api/companies/subscribe", {
          packageId,
          billingCycle: "MONTHLY",
        });
      }

      window.location.href = "/dashboard";
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || "Bedrijf opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-gradient py-12 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-brand-300/25 blur-3xl animate-liquid-sway" />
        <div className="absolute bottom-10 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-300/25 blur-3xl animate-liquid-sway" style={{ animationDelay: "-2s" }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto relative z-10"
      >
        <Card className="p-8 md:p-10 surface-card">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-brand-500/25">
              <Building2 size={42} />
            </div>
            <h1 className="text-2xl font-bold">Richt je bedrijf in</h1>
            <p className="text-slate-500 text-sm mt-1">Vul je bedrijfsgegevens in om verder te gaan.</p>
          </div>

          {error && (
            <div className="mb-5 rounded-xl bg-red-50 text-red-700 px-4 py-3 text-sm border border-red-100">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Bedrijfsnaam"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Input label="KVK-nummer" value={kvk} onChange={(e) => setKvk(e.target.value)} />
              <Input label="BTW-nummer" value={vat} onChange={(e) => setVat(e.target.value)} />
            </div>

            <Input label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />

            <Input label="Adres" value={address} onChange={(e) => setAddress(e.target.value)} />

            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Postcode" value={zip} onChange={(e) => setZip(e.target.value)} />
              <Input label="Plaats" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Logo</label>
              <label className="flex flex-col items-center justify-center gap-2 w-full h-28 border-2 border-dashed border-slate-300/70 rounded-2xl cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors bg-white/40">
                {logo ? (
                  <div className="flex items-center gap-2 text-emerald-600 font-medium">
                    <Check size={27} />
                    <span>{logo.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload size={33} className="text-slate-400" />
                    <span className="text-sm text-slate-500">Klik om logo te uploaden</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLogo(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            <Button type="submit" className="w-full" loading={loading}>
              Bedrijf opslaan
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
